/**
 * @file TradingDash.jsx
 * @description Provides trading dashboard interface and analytics
 *
 * @section Responsibilities
 * - Display trading dashboard
 * - Handle trading analytics
 * - Manage dashboard state
 * - Process trading data
 * - Handle dashboard errors
 * - Provide analytics controls
 *
 * @section Dependencies
 * - DashboardService: Dashboard management
 * - AnalyticsService: Analytics processing
 * - DataService: Data processing
 * - StateService: State management
 *
 * @section Integration Points
 * - Dashboard API: Dashboard operations
 * - Analytics System: Analytics processing
 * - Data System: Data processing
 * - Control System: Dashboard controls
 *
 * @section State Management
 * - dashboardState: Dashboard status
 * - analyticsState: Analytics status
 * - dataState: Data status
 * - errorState: Error handling
 *
 * @section Events
 * - onAnalytics: Analytics processing
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Object} dashboardConfig - Dashboard configuration
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialData - Initial dashboard data
 * @prop {Boolean} autoUpdate - Controls auto-updates
 */

import React, { useState, useCallback, useEffect } from "react";
import Widget from "../Widget";
import DgButton from "../../../../components/DgButton";
import Swal from "sweetalert2";
import Request from "../../../../utils/fetchUtils";
import TradingBody from "./TradingBody";
import { OrderClasses, OrderSides } from "./tradingVars";
import Loader from "../Loader";
import DgModal from "../../../../components/DgModal";
import { formatAmount } from "../../../../utils/formatAmount";
import { getFromSession } from "../../../../utils/sessionUtils";

function TradingDash({ strategy, closingPosition, handleFetchData }) {
  const [tradingType, setTradingType] = useState("standard");
  const [orderClass, setOrderClass] = useState("equity");
  const [symbol, setSymbol] = useState("");
  const [symbolData, setSymbolData] = useState([]);
  const [prefilledOrder, setPrefilledOrder] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeSymbol, setActiveSymbol] = useState({});

  const [strikeList, setStrikeList] = useState({});
  const [strikeOptionObjects, setStrikeOptionObjects] = useState([]);

  const [expiration, setExpiration] = useState();
  const [orderBody, setOrderBody] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    commission: 0,
    cost: 0,
    fees: 0,
    symbol: "",
    quantity: "",
    side: "",
    type: "",
    duration: "",
    result: "",
    order_cost: "",
    margin_change: 0,
    class: "",
  });

  const sessionRole = getFromSession("role");
  const isSupportRole = sessionRole === "support";

  const handleModalState = (open) => {
    setIsModalOpen(open);
  };

  const getExpiration = (exp) => {
    setExpiration(exp);
  };

  const getOrderData = (order) => {
    setOrderBody(order);
  };

  const handleSymbolChange = (event) => {
    setSymbol(event.target.value);
  };

  const handleOrderClassChange = (event) => {
    setOrderClass(event.target.value);
  };

  useEffect(() => {
    if (symbol) {
      getSymbolData();
    }
  }, [orderClass]);

  useEffect(() => {
    if (closingPosition) {
      setSymbol(closingPosition.symbol);
      prefillTradingForm(closingPosition.symbol, closingPosition.quantity);
    }
  }, [closingPosition]);

  useEffect(() => {
    if (expiration) {
      getSymbolStrikes();
    } else {
      setStrikeList(null);
    }
  }, [expiration]);

  const getSymbolData = useCallback(async (customSymbol) => {
    let symbolQuery;

    if (tradingType == "standard") {
      if (customSymbol) {
        symbolQuery = customSymbol;
      } else {
        symbolQuery = symbol;
      }
    } else {
      // Advanced symbols
    }

    setLoading(true);
    const res = await Request(
      `/trading/symbol?symbols=${symbolQuery}&orderClass=${orderClass}&strategyId=${strategy.strategyId}`
    );

    if (res.ok) {
      setLoading(false);
      setActiveSymbol(res.data[0]);
    } else {
      setLoading(false);
      Swal.fire({
        title: "Error consulting symbol",
        text: "Symbol does not exist",
        icon: "error",
      });
    }
  });

  const getSymbolStrikes = useCallback(async () => {
    let symbolQuery;

    if (tradingType == "standard") {
      symbolQuery = symbol;
    } else {
      // Advanced symbols
    }

    const res = await Request(
      `/trading/symbol/data?symbol=${symbolQuery}&expiration=${expiration}&type=strikes&strategyId=${strategy.strategyId}`
    );

    if (res.ok) {
      const strikesData = res.data?.options?.option;

      const filteredStrikes = [
        ...new Set(
          strikesData.map(function (strike) {
            return strike.strike;
          })
        ),
      ];

      setStrikeList(filteredStrikes);
      setStrikeOptionObjects(strikesData);
    }
  });

  const reset = () => {
    setSymbol("");
    setActiveSymbol("");
    setOrderBody({});
  };

  const orderValidation = (real) => {
    // Check if user has support role
    if (isSupportRole) {
      Swal.fire({
        title: "Access Denied",
        text: "Support users are not allowed to create orders",
        icon: "warning",
      });
      return;
    }

    if (Object.keys(orderBody).length != 0) {
      if (orderBody.class === "multileg") {
        const legs = orderBody.leg || [];
        const legsValid =
          legs.length >= 2 &&
          legs.every(
            (l) => l.option_symbol && l.side && l.quantity
          );
        const priceRequired =
          orderBody.type === "debit" || orderBody.type === "credit";
        const priceValid =
          !priceRequired ||
          (orderBody.price !== undefined &&
            orderBody.price !== null &&
            orderBody.price !== "");
        if (!orderBody.type || !orderBody.duration || !legsValid || !priceValid) {
          Swal.fire({
            title: "Error previewing order",
            text: priceValid
              ? "Please fill all leg fields (strike, type, side, quantity) for at least 2 legs"
              : "Please enter a price for debit/credit orders",
            icon: "error",
          });
          return;
        }
      } else if (
        Object.values(orderBody).some(
          (value) => value === "" || value === null || value === undefined
        )
      ) {
        Swal.fire({
          title: "Error previewing order",
          text: "Please make sure to fill out all the data",
          icon: "error",
        });
        return;
      }
      createOrder(real);
    } else {
      Swal.fire({
        title: "Error previewing order",
        text: "Please fill out all the data",
        icon: "error",
      });
    }
  };

  const createOrder = async (real) => {
    // Additional security check
    if (isSupportRole) {
      Swal.fire({
        title: "Access Denied",
        text: "Support users are not allowed to create orders",
        icon: "warning",
      });
      return;
    }

    const res = await Request(
      `/trading/strategy/${strategy.strategyId}/orders?preview=true`,
      orderBody,
      "POST"
    );

    if (res.ok) {
      const orderPreviewCost = res.data?.order_cost;

      if (real) {
        const orderRes = await Request(
          `/trading/strategy/${strategy.strategyId}/orders`,
          {
            ...orderBody,
            order_cost: orderPreviewCost,
          },
          "POST"
        );

        if (orderRes.ok && orderRes.data) {
          setIsModalOpen(false);
          Swal.fire({
            title: "Order created",
            text: "Order created successfully",
            icon: "success",
          });
          reset();
          handleFetchData();
        }
      } else {
        setIsModalOpen(!isModalOpen);
        setModalData(res.data);
      }
    } else {
      Swal.fire({
        title: "Error creating order",
        text: res.error || res.message || "There was an error creating the order",
        icon: "error",
      });
    }
  };

  const prefillTradingForm = (closingSymbol, closingQty) => {
    getSymbolData(closingSymbol).then(() => {
      let tmpOrder = {
        quantity: 0,
        side: "",
      };
      if (closingQty > 0) {
        tmpOrder.side = OrderSides.SELL.value;
        tmpOrder.quantity = closingQty;
      } else {
        tmpOrder.side = OrderSides.BUY_TO_COVER.value;
        tmpOrder.quantity = closingQty * -1;
      }
      setPrefilledOrder(tmpOrder);
    });
  };

  return (
    <>
      <Widget name={"trading-dash"} width={"full-width"}>
        {loading && <Loader loading={loading}></Loader>}
        <h2 className="left-title">Trading</h2>
        {/* Show warning message for support users */}
        {isSupportRole && (
          <div className="alert-warning" style={{ 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            padding: '10px', 
            borderRadius: '5px', 
            marginBottom: '15px',
            border: '1px solid #ffeaa7' 
          }}>
            <strong>Restricted Access:</strong> Support users can only view trading information, orders cannot be created.
          </div>
        )}
        <div className="trading-dash">
          <div className="trading-info">
            <div className="tab-group">
              <div className="tab active">
                <p>Standard Trading</p>
              </div>
              <div className="tab">{/* <p>Advanced Trading</p> */}</div>
            </div>
            <div className="row">
              <div className="search">
                <input
                  style={{ textTransform: "uppercase" }}
                  type="text"
                  placeholder="Enter an underlying symbol"
                  value={symbol}
                  onChange={handleSymbolChange}
                  disabled={isSupportRole}
                />
                <img
                  src="/assets/icons/search.svg"
                  alt=""
                  onClick={() => {
                    if (!isSupportRole) {
                      getSymbolData();
                    }
                  }}
                  style={{ 
                    opacity: isSupportRole ? 0.5 : 1, 
                    cursor: isSupportRole ? 'not-allowed' : 'pointer' 
                  }}
                />
              </div>
              <div className="form-field first">
                <select
                  name=""
                  onChange={handleOrderClassChange}
                  value={orderClass}
                  disabled={isSupportRole}
                >
                  {Object.keys(OrderClasses).map((oClass) => (
                    <option
                      key={OrderClasses[oClass].value}
                      value={OrderClasses[oClass].value}
                    >
                      {OrderClasses[oClass].name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* TODO check if advanced and create advancedTradingBody*/}
            <TradingBody
              orderClass={orderClass}
              data={activeSymbol}
              setOrderBody={setOrderBody}
              getExpiration={getExpiration}
              prefilledOrder={prefilledOrder}
              updatedStrikes={strikeList}
              strikeOptionObjects={strikeOptionObjects}
              disabled={isSupportRole}
            />
            <p className="info">
              Quotes for underlying Index values (e.g. SPX, VIX) are delayed 15
              minutes.
              <br />
              Commission/fees do not include regulatory or pass-through fees.
              <br />
              Stop Order Disclosure
            </p>
            <div className="buttons">
              <div>
                <DgButton
                  category="primary"
                  size="medium"
                  text="Submit"
                  action={() => {
                    orderValidation(true);
                  }}
                  disabled={isSupportRole}
                ></DgButton>
                <DgButton
                  category="secondary"
                  size="medium"
                  text="Preview"
                  action={() => {
                    orderValidation(false);
                  }}
                  disabled={isSupportRole}
                ></DgButton>
              </div>
              <DgButton
                category="tertiary"
                size="medium"
                text="Reset"
                action={() => {
                  reset();
                }}
                disabled={isSupportRole}
              ></DgButton>
            </div>
          </div>
          <div className="market-info">
            <div className="market">
              {/* <p>Market is open.</p> */}
              <DgButton
                category="secondary"
                size="small"
                text="Preview"
                action={() => {
                  orderValidation(false);
                }}
                disabled={isSupportRole}
              ></DgButton>
            </div>
            <div className="symbol">
              {Object.keys(activeSymbol).length === 0 ? (
                <span>Fill in the trade ticket to see pricing</span>
              ) : (
                <div>
                  <div className="line">
                    <span className="name">{activeSymbol?.symbol}</span>
                    <div>
                      <span className="cost">${activeSymbol?.last}</span>
                      <span
                        className={
                          "gainloss " +
                          (activeSymbol?.change < 0 ? "negative" : "")
                        }
                      >
                        (${activeSymbol?.change})
                      </span>
                    </div>
                  </div>
                  <div className="details">
                    <div className="detail">
                      <span className="prop">Bid</span>
                      <span className="val">
                        ${activeSymbol?.bid} x {activeSymbol?.bidsize}
                      </span>
                    </div>
                    <div className="detail">
                      <span className="prop">Ask</span>
                      <span className="val">
                        ${activeSymbol?.ask} x {activeSymbol?.asksize}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DgModal
          modalState={isModalOpen}
          handleModalState={() => {
            setIsModalOpen(!isModalOpen);
          }}
        >
          <div className="modal-content preview">
            <h1 className="title">Preview order</h1>
            <div className="flex">
              <div className="data-group">
                <p className="data-field">
                  Commission: $ {formatAmount(modalData?.commission)}
                </p>
                <p className="data-field">
                  Cost: $ {formatAmount(modalData?.cost)}
                </p>
                <p className="data-field">
                  Fees: $ {formatAmount(modalData?.fees)}
                </p>
                <p className="data-field">
                  Order cost: $ {formatAmount(modalData?.order_cost)}
                </p>
                <p className="data-field">
                  Margin change: $ {formatAmount(modalData?.margin_change)}
                </p>
                <p className="data-field">Class: {modalData?.class}</p>
              </div>
              <div className="data-group">
                <p className="data-field">Symbol: {modalData?.symbol}</p>
                <p className="data-field">Quantity: {modalData?.quantity}</p>
                <p className="data-field">Side: {modalData?.side}</p>
                <p className="data-field">Type: {modalData?.type}</p>
                <p className="data-field">Duration: {modalData?.duration}</p>
              </div>
            </div>
            <div className="flex">
              <DgButton
                category="primary"
                size="large"
                text="Submit order"
                action={() => {
                  orderValidation(true);
                }}
                disabled={isSupportRole}
              ></DgButton>
              <DgButton
                category="secondary"
                size="large"
                text="Cancel"
                action={() => {
                  handleModalState(!isModalOpen);
                }}
              ></DgButton>
            </div>
          </div>
        </DgModal>
      </Widget>
    </>
  );
}

export default TradingDash;
